import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { TranslateContext } from "../../context/TranslateContext";
import { useCustomTheme } from "../../utils/useCustomTheme";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import Content from "./Content";
import { useHistory, useLocation } from "react-router-dom";

const drawerWidth = 260;

const AdminDashboard = () => {
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const { toggleColorMode, isDark } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const history = useHistory();
  const location = useLocation();

  // ── Read initial menu from ?page= query param ──────────────────
  const getPageFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("page") || "dashboard";
  };

  const [selectedMenu, setSelectedMenu] = React.useState(getPageFromUrl);
  const [openSubmenus, setOpenSubmenus] = React.useState({});
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // ── Sync URL when menu changes ─────────────────────────────────
  const navigateTo = (menuId) => {
    history.replace({ pathname: "/admin", search: `?page=${menuId}` });
    setSelectedMenu(menuId);
  };

  // ── Also react if browser back/forward is used ─────────────────
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get("page") || "dashboard";
    setSelectedMenu(page);
  }, [location.search]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleMenuClick = (menuId, hasSubmenu) => {
    if (hasSubmenu) {
      setOpenSubmenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
    } else {
      navigateTo(menuId);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleSubmenuClick = (submenuId) => {
    navigateTo(submenuId);
    if (isMobile) setMobileOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Sidebar
        lang={lang}
        theme={theme}
        isMobile={isMobile}
        selectedMenu={selectedMenu}
        openSubmenus={openSubmenus}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        handleMenuClick={handleMenuClick}
        handleSubmenuClick={handleSubmenuClick}
        isDark={isDark}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, md: 0 },
        }}
      >
        <TopBar
          lang={lang}
          theme={theme}
          isMobile={isMobile}
          isDark={isDark}
          selectedMenu={selectedMenu}
          toggleColorMode={toggleColorMode}
          handleDrawerToggle={handleDrawerToggle}
        />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Content selectedMenu={selectedMenu} />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
